// api/auth.ts
import { ApiError, apiFetch, apiJson, setToken } from './client';
import { trackClientEvent } from '../observability/client-events';

function recordAuthSuccess(code: 'invite_completed' | 'registration_completed' | 'login_completed'): void {
  void trackClientEvent({ eventKind: 'journey', surface: 'auth', outcome: 'success', code });
}

function isExactLogoutSuccess(body: unknown): body is { ok: true } {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return false;
  const keys = Object.keys(body);
  return keys.length === 1 && keys[0] === 'ok' && (body as Record<string, unknown>).ok === true;
}

export interface ExchangeInviteCodeResponse {
  token: string;
  studentId: string;
  expiresAt: number;
}

/** Plaintext recovery codes exist only in this one response and in component
 * state. They must never be written to browser storage or analytics. */
export interface RecoveryKit {
  generation: string;
  codes: string[];
  expiresAt: string;
}

export type RegisterStudentResponse = ExchangeInviteCodeResponse & (
  | { recoveryKit: RecoveryKit; recoveryKitUnavailable?: false }
  | { recoveryKit: null; recoveryKitUnavailable: true }
);

export const authApi = {
  async exchangeInviteCode(body: {
    code: string;
    firstName: string;
    ageBracket: 'under_18' | 'adult';
    examDate?: string;
  }): Promise<ExchangeInviteCodeResponse> {
    const res = await apiJson<ExchangeInviteCodeResponse>('/api/auth/exchange-invite-code', {
      method: 'POST',
      body: JSON.stringify(body),
    }, 'public');
    setToken(res.token);
    recordAuthSuccess('invite_completed');
    return res;
  },

  // Pilot-readiness P3 (founder B1+B2, 2026-07-07) — join-link registration
  // + username/password login. Same token storage as the invite flow.
  async joinInfo(slug: string): Promise<{ schoolName: string }> {
    return apiJson<{ schoolName: string }>(
      `/api/auth/join-info?slug=${encodeURIComponent(slug)}`,
      { method: 'GET' },
      'public',
    );
  },

  async registerStudent(body: {
    joinSlug: string;
    firstName: string;
    username: string;
    password: string;
    ageBracket: 'under_18' | 'adult';
    examDate?: string;
    consentAccepted: true;
    consentTextVersion: string;
  }): Promise<RegisterStudentResponse> {
    const res = await apiJson<RegisterStudentResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }, 'public');
    setToken(res.token);
    recordAuthSuccess('registration_completed');
    return res;
  },

  async loginStudent(body: { username: string; password: string }): Promise<ExchangeInviteCodeResponse> {
    const res = await apiJson<ExchangeInviteCodeResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }, 'public');
    setToken(res.token);
    recordAuthSuccess('login_completed');
    return res;
  },

  async recoverStudent(body: {
    username: string;
    code: string;
    newPassword: string;
  }): Promise<{ status: 'accepted' }> {
    return apiJson<{ status: 'accepted' }>('/api/auth/recover', {
      method: 'POST',
      body: JSON.stringify(body),
    }, 'public');
  },

  async rotateRecoveryKit(body: { currentPassword: string }): Promise<RecoveryKit> {
    return apiJson<RecoveryKit>('/api/auth/recovery-kit', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async logout(): Promise<void> {
    const response = await apiFetch('/api/auth/logout', { method: 'POST' });
    let body: unknown = null;
    try {
      body = await response.json() as unknown;
    } catch {
      throw new ApiError(response.status, null, `API ${response.status} invalid JSON at /api/auth/logout`);
    }
    if (response.status === 200 && isExactLogoutSuccess(body)) return;
    throw new ApiError(response.status, body, `API ${response.status} invalid logout response`);
  },
};
